import { Core, WebViewerInstance } from '@pdftron/webviewer';
import PDFAnnotationManager from 'components/PDFViewer/PDFAnnotationManager';
import PDFManagerFactory from 'pdftron/PDFManagerFactory';
import WebViewerWrapper from 'pdftron/core/WebViewerWrapper';
import {
  CropZoneCustomData,
  DocumentTypes,
  ManualAlignZoneCustomData,
  PDFTronTools,
  Crop,
  RectangularCrop,
  PolygonCrop,
} from 'types';
import {
  pointInPolygon,
  getPagePolygonZone,
  getAllPagePolygonZones,
  cursorIsInsidePolygonZone,
  polygonContainsAnnotation,
} from './PolygonToolUtils';
import { getPageRotation } from './labelPositionUtils';

export enum CursorStyle {
  DEFAULT = 'default',
  POINTER = 'pointer',
  CROSSHAIR = 'crosshair',
  NOT_ALLOWED = 'grab',
}

export const setCursorStyle = (
  cursorStyle: string,
  iframeDoc: { style: { removeProperty: (arg0: string) => void; cursor: string } },
) => {
  if (cursorStyle === CursorStyle.DEFAULT) {
    iframeDoc.style.removeProperty('cursor');
  } else {
    iframeDoc.style.cursor = cursorStyle;
  }
};

export function resetCursors() {
  const sourceViewer = PDFManagerFactory.getViewer(DocumentTypes.source);
  const targetViewer = PDFManagerFactory.getViewer(DocumentTypes.target);

  [sourceViewer, targetViewer].forEach((wrapper) => {
    if (!wrapper) return;
    const { instance } = wrapper as WebViewerWrapper;
    const pageCount = instance.Core.documentViewer.getPageCount();
    for (let i = 1; i <= pageCount; i++) {
      const iframeDoc = getIframeDocument(wrapper, i);
      iframeDoc?.style?.removeProperty('cursor');
    }
  });
}

export function getCursorPageNumber(
  instance: WebViewerInstance,
  toolObject: { getMouseLocation: (e: MouseEvent) => Core.DisplayMode.Point },
  event: MouseEvent,
) {
  const displayMode = instance.Core.documentViewer.getDisplayModeManager().getDisplayMode();
  const windowCoordinates = toolObject.getMouseLocation(event);
  return displayMode.getSelectedPages(windowCoordinates, windowCoordinates).first;
}

export function getPagePointFromMouseEvent(
  mouseEvent: { clientX: number; clientY: number },
  wrapper: WebViewerWrapper,
  currentPage?: number,
) {
  const { instance } = wrapper;
  const documentViewer = instance.Core.documentViewer;
  const displayMode = documentViewer.getDisplayModeManager().getDisplayMode();
  const scrollViewElement = documentViewer.getScrollViewElement();
  const scrollRect = scrollViewElement.getBoundingClientRect();
  const windowPoint = {
    x: mouseEvent.clientX - scrollRect.left + scrollViewElement.scrollLeft,
    y: mouseEvent.clientY - scrollRect.top + scrollViewElement.scrollTop,
  };
  const page = currentPage ?? displayMode.getSelectedPages(windowPoint, windowPoint).first;
  if (!page) return;
  return displayMode.windowToPage(windowPoint, page);
}

export function isPagePointInsideCropZone(
  pagePoint: { x: number; y: number },
  cropZone: { getRect: () => { x1: number; y1: number; x2: number; y2: number } },
  zoomLevel: number,
  addBuffer = false,
  pageRotation = 0,
) {
  const { x1, y1, x2, y2 } = cropZone.getRect();
  const edgeBuffer = 5;
  // The popup buffer follows the page rotation: it sits along the y-axis at 0°/180°
  // and along the x-axis at 90°/270° (page displayed horizontally).
  const popupBuffer = addBuffer ? 75 / zoomLevel : edgeBuffer;
  const isPageHorizontal = pageRotation % 2 !== 0;
  const xBuffer = isPageHorizontal ? popupBuffer : edgeBuffer;
  const yBuffer = isPageHorizontal ? edgeBuffer : popupBuffer;
  return (
    pagePoint.x >= x1 - xBuffer &&
    pagePoint.x <= x2 + xBuffer &&
    pagePoint.y >= y1 - yBuffer &&
    pagePoint.y <= y2 + yBuffer
  );
}

export function cursorIsInsideCropZone(
  mouseEvent: { clientX: number; clientY: number },
  cropZone: { getRect: () => { x1: number; y1: number; x2: number; y2: number } },
  wrapper: WebViewerWrapper,
  currentPage: number,
  addBuffer = false,
) {
  const { instance } = wrapper;
  const zoomLevel = instance.UI.getZoomLevel();
  const pagePoint = getPagePointFromMouseEvent(mouseEvent, wrapper, currentPage);
  if (!pagePoint) return false;
  const pageRotation = getPageRotation(instance, currentPage);
  return isPagePointInsideCropZone(pagePoint, cropZone, zoomLevel, addBuffer, pageRotation);
}

export function getPageCropZone(instance: WebViewerInstance | undefined, currentPage: number) {
  if (!instance) return;
  return instance.Core.annotationManager.getAnnotationsList().find((annot: any) => {
    return annot.getCustomData(CropZoneCustomData.cropZoneAnnotation) === 'true' && annot.PageNumber === currentPage;
  });
}

export function getAllPageCropZones(instance: WebViewerInstance | undefined, currentPage: number) {
  if (!instance) return [];
  return instance.Core.annotationManager.getAnnotationsList().filter((annot: any) => {
    return annot.getCustomData(CropZoneCustomData.cropZoneAnnotation) === 'true' && annot.PageNumber === currentPage;
  });
}

export function getAllPageManualAlignZones(instance: WebViewerInstance | undefined, currentPage: number) {
  if (!instance) return [];
  return instance.Core.annotationManager.getAnnotationsList().filter((annot: any) => {
    return (
      annot.getCustomData(ManualAlignZoneCustomData.manualAlignZoneAnnotation) === 'true' &&
      annot.PageNumber === currentPage
    );
  });
}

export function getAllPageRectangularCropZones(instance: WebViewerInstance | undefined, currentPage: number) {
  const crops = getAllPageCropZones(instance, currentPage);
  const manualAlignZones = getAllPageManualAlignZones(instance, currentPage);
  return [...crops, ...manualAlignZones];
}

/** Align zones are checked first so one sitting on a crop receives the hover. */
export function preferredRectangularZone<T>(
  alignZones: readonly T[],
  cropZones: readonly T[],
  containsPoint: (zone: T) => boolean,
): T | undefined {
  return alignZones.find(containsPoint) ?? cropZones.find(containsPoint);
}

/**
 * Hover selects a zone when nothing else is selected.
 * An align zone also takes the selection from a crop underneath it.
 */
export function shouldTakeHoverSelection(
  hoveredId: string | undefined,
  selectedZoneId: string | undefined,
  isOtherAnnotationSelected: boolean,
  hoveredIsAlignZone: boolean,
): boolean {
  if (!hoveredId || isOtherAnnotationSelected) return false;
  if (!selectedZoneId) return true;
  return hoveredIsAlignZone && selectedZoneId !== hoveredId;
}

/**
 * Back-to-front order for one page. The overlay drawer stays at the back,
 * then other crops and polygons, then align zones, then every other annotation.
 */
export function stackPageAnnotationsForSelection<T extends { Id: string }>(
  pageAnnotations: readonly T[],
  drawerId: string | undefined,
  isManualAlign: (annotation: T) => boolean,
  isCropOrPolygon: (annotation: T) => boolean,
): T[] {
  const drawer: T[] = [];
  const zones: T[] = [];
  const alignZones: T[] = [];
  const rest: T[] = [];

  for (const annotation of pageAnnotations) {
    if (drawerId && annotation.Id === drawerId) {
      drawer.push(annotation);
      continue;
    }
    if (isManualAlign(annotation)) {
      alignZones.push(annotation);
      continue;
    }
    if (isCropOrPolygon(annotation)) {
      zones.push(annotation);
      continue;
    }
    rest.push(annotation);
  }

  return [...drawer, ...zones, ...alignZones, ...rest];
}

export function getIframeDocument(wrapper: WebViewerWrapper, currentPage: number) {
  return wrapper.getIframeWindow().document.getElementById(`pageContainer${currentPage.toString()}`);
}

export function disableToolOutsideCropZones(tool: any, wrapper: WebViewerWrapper) {
  const { instance } = wrapper;

  const defaultMouseMoveEvent = tool.mouseMove.bind(tool);
  tool.mouseMove = (e: MouseEvent) => {
    // Check if there is a crop zone on the page
    const currentPage = getCursorPageNumber(instance, tool, e);
    if (!currentPage) return; // this only happens if cursor is in the space between pages

    // First check for crop zones (including ManualAlignZones)
    const cropZones = getAllPageRectangularCropZones(instance, currentPage);
    const selectedAnnotation = instance.Core.annotationManager.getSelectedAnnotations()[0];

    if (cropZones.length > 0) {
      const iframeDoc = getIframeDocument(wrapper, currentPage);
      if (!iframeDoc) return;

      // Check if cursor is inside any crop zone
      const isInsideAnyCropZone = cropZones.some(
        (cropZone) =>
          cropZone.Id === selectedAnnotation?.Id || cursorIsInsideCropZone(e, cropZone, wrapper, currentPage),
      );

      if (isInsideAnyCropZone) {
        const selectedTool = instance.Core.documentViewer.getToolMode();
        setCursorStyle(selectedTool.cursor, iframeDoc);
        defaultMouseMoveEvent(e);
      } else {
        setCursorStyle(CursorStyle.NOT_ALLOWED, iframeDoc);
      }
    } else {
      // If no crop zones, check for polygon zones (can be multiple)
      const polygonZones = getAllPagePolygonZones(instance, currentPage);

      if (polygonZones.length > 0) {
        const iframeDoc = getIframeDocument(wrapper, currentPage);
        if (!iframeDoc) return;

        // Check if cursor is inside any polygon zone
        const isInsideAnyPolygonZone = polygonZones.some(
          (polygonZone) =>
            polygonZone.Id === selectedAnnotation?.Id ||
            cursorIsInsidePolygonZone(e, polygonZone as Core.Annotations.PolygonAnnotation, wrapper, currentPage),
        );

        if (isInsideAnyPolygonZone) {
          const selectedTool = instance.Core.documentViewer.getToolMode();
          setCursorStyle(selectedTool.cursor, iframeDoc);
          defaultMouseMoveEvent(e);
        } else {
          setCursorStyle(CursorStyle.NOT_ALLOWED, iframeDoc);
        }
      } else {
        defaultMouseMoveEvent(e);
      }
    }
  };
}

// Used to prevent accidentally selecting crop and polygon zones while using other tools
export function setCropZonesAreListable(areListable: boolean) {
  const getPolygonAndCropAnnotations = (docType: DocumentTypes) => {
    const annotMgr = PDFAnnotationManager.getInstance(docType);
    const crops = annotMgr?.getAnnotationsList()?.filter((annot) => annot.ToolName === PDFTronTools.CROP) ?? [];
    const manualAlignZones =
      annotMgr
        ?.getAnnotationsList()
        ?.filter((annot) => annot.getCustomData(ManualAlignZoneCustomData.manualAlignZoneAnnotation) === 'true') ?? [];
    const polygons = annotMgr?.getAnnotationsList()?.filter((annot) => annot.ToolName === PDFTronTools.POLYGON) ?? [];
    return [...crops, ...manualAlignZones, ...polygons];
  };

  const sourceZones = getPolygonAndCropAnnotations(DocumentTypes.source);
  const targetZones = getPolygonAndCropAnnotations(DocumentTypes.target);
  const allZones = [...sourceZones, ...targetZones];

  allZones.forEach((zone) => {
    zone.Listable = areListable;
  });
}

// Checks if a crop zone contains annotations, and if so, it locks it
export function manageCropZoneLocking(cropZone: Core.Annotations.Annotation, documentType: DocumentTypes) {
  const instance = PDFManagerFactory.getViewer(documentType)?.instance;
  if (!instance) return;

  // Check if THIS specific crop zone contains any annotations (markups)
  cropZone.Locked = cropContainsAnnotation(instance, cropZone);
}

// Unified function to manage locking for both crop and polygon zones
export function manageZoneLocking(zone: Core.Annotations.Annotation, documentType: DocumentTypes) {
  const instance = PDFManagerFactory.getViewer(documentType)?.instance;
  if (!instance) return;

  // Check if this is a polygon zone or crop zone
  const isPolygonZone = zone.getCustomData('polygonAnnotation') === 'true';

  if (isPolygonZone) {
    zone.Locked = polygonContainsAnnotation(instance, zone as Core.Annotations.PolygonAnnotation);
  } else {
    // Crop zone
    zone.Locked = cropContainsAnnotation(instance, zone);
  }
}

export function annotationIsOutsideExistingCropZone(
  annotation: Core.Annotations.Annotation,
  documentType: DocumentTypes,
) {
  const instance = PDFManagerFactory.getViewer(documentType)?.instance;

  // Boundary tolerance in PDF points (3 points ≈ 4 pixels at 96 DPI, or 3-4 pixels at typical screen resolutions).
  // This allows annotations that are exactly on or very close to crop zone boundaries to pass validation.
  // Especially important for snap-to-content which creates exact boundary alignments.
  const BOUNDARY_TOLERANCE = 3;

  const cropZones = getAllPageRectangularCropZones(instance, annotation.PageNumber);
  if (cropZones.length > 0) {
    const annotationRect = annotation.getRect();

    // Check if the annotation is inside at least one crop zone (with tolerance)
    const isInsideAnyCropZone = cropZones.some((cropZone) => {
      const cropZoneRect = cropZone.getRect();
      return (
        annotationRect.x1 >= cropZoneRect.x1 - BOUNDARY_TOLERANCE &&
        annotationRect.x2 <= cropZoneRect.x2 + BOUNDARY_TOLERANCE &&
        annotationRect.y1 >= cropZoneRect.y1 - BOUNDARY_TOLERANCE &&
        annotationRect.y2 <= cropZoneRect.y2 + BOUNDARY_TOLERANCE
      );
    });

    // If not inside any crop zone, it's outside all of them
    return !isInsideAnyCropZone;
  }

  // If no crop zone, check for polygon zone
  const polygonZone = getPagePolygonZone(instance, annotation.PageNumber);
  if (polygonZone) {
    const polygonPoints = (polygonZone as Core.Annotations.PolygonAnnotation).getPath();
    if (!polygonPoints || polygonPoints.length === 0) {
      return false;
    }

    const annotationRect = annotation.getRect();

    // Check if all corners of the annotation are within the polygon (with tolerance)
    const corners = [
      { x: annotationRect.x1, y: annotationRect.y1 }, // top-left
      { x: annotationRect.x2, y: annotationRect.y1 }, // top-right
      { x: annotationRect.x2, y: annotationRect.y2 }, // bottom-right
      { x: annotationRect.x1, y: annotationRect.y2 }, // bottom-left
    ];

    // Shrink each corner slightly inward to apply tolerance (make the annotation "smaller" for checking)
    const tolerantCorners = corners.map((corner, idx) => {
      const isLeft = idx === 0 || idx === 3;
      const isTop = idx === 0 || idx === 1;
      return {
        x: corner.x + (isLeft ? BOUNDARY_TOLERANCE : -BOUNDARY_TOLERANCE),
        y: corner.y + (isTop ? BOUNDARY_TOLERANCE : -BOUNDARY_TOLERANCE),
      };
    });

    // If any corner is outside the polygon, the annotation extends outside
    return tolerantCorners.some((corner) => !pointInPolygon(corner, polygonPoints));
  }

  return false;
}

export function isRectangularCrop(crop: Crop): crop is RectangularCrop {
  return 'x1' in crop && 'x2' in crop && 'y1' in crop && 'y2' in crop;
}

export function isPolygonCrop(crop: Crop): crop is PolygonCrop {
  return 'points' in crop && Array.isArray((crop as PolygonCrop).points) && (crop as PolygonCrop).points.length >= 3;
}

export function cropContainsAnnotation(
  instance: WebViewerInstance,
  cropAnnotation: Core.Annotations.Annotation,
): boolean {
  if (!cropAnnotation) return false;

  const pageNumber = cropAnnotation.PageNumber;
  const cropRect = cropAnnotation.getRect();

  const pageAnnotations = instance.Core.annotationManager
    .getAnnotationsList()
    .filter((annotation) => annotation.PageNumber === pageNumber && annotation.Id !== cropAnnotation.Id);

  for (const annotation of pageAnnotations) {
    const isUserAnnotation =
      annotation.ToolName === PDFTronTools.CROSSOUT ||
      annotation.ToolName === PDFTronTools.MARQUEE_ZONE ||
      annotation.ToolName === PDFTronTools.ZONE;

    if (isUserAnnotation) {
      // Check if the annotation is actually inside this specific crop zone's bounds
      const annotationRect = annotation.getRect();
      const isInsideThisCrop =
        annotationRect.x1 >= cropRect.x1 &&
        annotationRect.x2 <= cropRect.x2 &&
        annotationRect.y1 >= cropRect.y1 &&
        annotationRect.y2 <= cropRect.y2;

      if (isInsideThisCrop) {
        return true;
      }
    }
  }
  return false;
}

// Get all zones (crops, manual align, and polygons) on a page, sorted by ID (creation order)
export function getAllPageCropAndPolygonZones(instance: WebViewerInstance | undefined, pageNumber: number) {
  const rectangularZones = getAllPageRectangularCropZones(instance, pageNumber);
  const polygons = getAllPagePolygonZones(instance, pageNumber);
  const allZones = [...rectangularZones, ...polygons];
  allZones.sort((a, b) => a.Id.localeCompare(b.Id));
  return allZones;
}

// Draw a shared overlay with holes for all crop and polygon zones
// The annotation that calls this is moved to the back via bringToBack(),
// ensuring the overlay renders behind all other crop/polygon annotations
export function drawSharedOverlay(
  ctx: CanvasRenderingContext2D,
  pageWidth: number,
  pageHeight: number,
  cropZones: Core.Annotations.Annotation[],
  polygonZones: Core.Annotations.Annotation[],
) {
  ctx.save();

  // Draw gray overlay for entire page first
  ctx.fillStyle = 'rgba(64, 64, 64, 0.6)';
  ctx.fillRect(0, 0, pageWidth, pageHeight);

  // Cut out holes for all crop zones
  ctx.globalCompositeOperation = 'destination-out';
  cropZones.forEach((crop) => {
    const box = crop.getRect();
    ctx.fillStyle = 'rgba(0, 0, 0, 1)';
    ctx.fillRect(box.x1, box.y1, box.x2 - box.x1, box.y2 - box.y1);
  });

  // Cut out holes for all polygon zones
  polygonZones.forEach((polygon) => {
    const points = (polygon as Core.Annotations.PolygonAnnotation).getPath();
    if (points && points.length > 0) {
      ctx.fillStyle = 'rgba(0, 0, 0, 1)';
      ctx.beginPath();
      ctx.moveTo(points[0].x, points[0].y);
      for (let i = 1; i < points.length; i++) {
        ctx.lineTo(points[i].x, points[i].y);
      }
      ctx.closePath();
      ctx.fill();
    }
  });

  ctx.restore();
}

// Store cached overlay canvas by documentType-pageNumber key
const overlayCache = new Map<string, HTMLCanvasElement>();

// Store zone configuration signatures to detect when overlay needs regeneration
const zoneSignatureByPage = new Map<string, string>();

// Store original draw for ManualAlignZones to avoid wrapper chaining (overlay drawn multiple times)
const manualAlignOriginalDrawMap = new WeakMap<
  Core.Annotations.Annotation,
  (ctx: CanvasRenderingContext2D, pageMatrix?: any, pageRotation?: number, zoomLevel?: number) => void
>();

// Generate a signature for the current zone configuration
function generateZoneSignature(
  cropZones: Core.Annotations.Annotation[],
  polygonZones: Core.Annotations.Annotation[],
): string {
  const cropSig = cropZones
    .map((crop) => {
      const rect = crop.getRect();
      return `${crop.Id}:${rect.x1},${rect.y1},${rect.x2},${rect.y2}`;
    })
    .join('|');

  const polygonSig = polygonZones
    .map((polygon) => {
      const points = (polygon as Core.Annotations.PolygonAnnotation).getPath();
      if (!points || points.length === 0) {
        return `${polygon.Id}:`;
      }
      const pointsStr = points.map((p) => `${p.x},${p.y}`).join(',');
      return `${polygon.Id}:${pointsStr}`;
    })
    .join('|');

  return `crops:${cropSig}||polygons:${polygonSig}`;
}

// Unified function to refresh ALL zone overlays (crops, manual align, and polygons) on a page
// This updates draw functions for crop and polygon zone types; ManualAlignZones keep their own draw
export function updateCropAndPolygonOverlays(instance: WebViewerInstance, pageNumber: number, refresh = true): void {
  const documentViewer = instance.Core.documentViewer;
  const annotationManager = instance.Core.annotationManager;
  const pageHeight = documentViewer.getPageHeight(pageNumber);
  const pageWidth = documentViewer.getPageWidth(pageNumber);

  if (!pageHeight || !pageWidth) return;

  // Get crop, manual align, and polygon zones separately
  const cropZones = getAllPageCropZones(instance, pageNumber);
  const manualAlignZones = getAllPageManualAlignZones(instance, pageNumber);
  const polygonZones = getAllPagePolygonZones(instance, pageNumber);
  const rectangularZones = [...cropZones, ...manualAlignZones];

  // Combine and sort to get all zones in consistent order (so the one responsible for the shared overlay doesn't change)
  const allZones = [...cropZones, ...manualAlignZones, ...polygonZones];
  allZones.sort((a, b) => a.Id.localeCompare(b.Id));

  const documentType =
    PDFManagerFactory.getViewer(DocumentTypes.source)?.instance === instance
      ? DocumentTypes.source
      : DocumentTypes.target;
  const cacheKey = `${documentType}-${pageNumber}`;

  if (allZones.length === 0) {
    // Clear cache if no zones
    overlayCache.delete(cacheKey);
    zoneSignatureByPage.delete(cacheKey);
    return;
  }

  // A crop or polygon draws the page overlay when one exists, so an align zone
  // is not pinned behind a crop that covers the same area.
  const overlayCandidates = [...cropZones, ...polygonZones].sort((a, b) => a.Id.localeCompare(b.Id));
  const firstAnnotation = overlayCandidates[0] ?? allZones[0];
  const firstAnnotationId = firstAnnotation.Id;

  const pageAnnotations = annotationManager
    .getAnnotationsList()
    .filter((annotation) => annotation.PageNumber === pageNumber);
  const cropOrPolygonIds = new Set([...cropZones, ...polygonZones].map((zone) => zone.Id));
  const stacked = stackPageAnnotationsForSelection(
    pageAnnotations,
    firstAnnotationId,
    (annotation) => annotation.getCustomData(ManualAlignZoneCustomData.manualAlignZoneAnnotation) === 'true',
    (annotation) => cropOrPolygonIds.has(annotation.Id),
  );
  const stackChanged = stacked.some((annotation, index) => annotation.Id !== pageAnnotations[index]?.Id);
  if (stackChanged && stacked[0]) {
    annotationManager.bringToBack(stacked[0]);
    for (let index = 1; index < stacked.length; index += 1) {
      annotationManager.bringToFront(stacked[index]);
    }
  }

  // Check if zone configuration has changed by comparing signatures
  const currentSignature = generateZoneSignature(rectangularZones, polygonZones);
  const previousSignature = zoneSignatureByPage.get(cacheKey);

  // Only regenerate the overlay canvas if the zone configuration has changed
  if (currentSignature !== previousSignature) {
    // Pre-render the overlay to an offscreen canvas (cache it)
    const offscreenCanvas = document.createElement('canvas');
    offscreenCanvas.width = pageWidth;
    offscreenCanvas.height = pageHeight;
    const offscreenCtx = offscreenCanvas.getContext('2d');

    if (offscreenCtx) {
      drawSharedOverlay(offscreenCtx, pageWidth, pageHeight, rectangularZones, polygonZones);
      overlayCache.set(cacheKey, offscreenCanvas);
      zoneSignatureByPage.set(cacheKey, currentSignature);
    }
  }

  // Update ALL crop zones on the page (ManualAlignZones keep their own draw)
  cropZones.forEach((crop) => {
    crop.draw = (ctx: CanvasRenderingContext2D) => {
      // Check if this zone should draw the overlay (first by ID)
      const shouldDrawOverlay = crop.Id === firstAnnotationId;

      if (shouldDrawOverlay) {
        // Draw the cached overlay using the correct cache key
        const cachedOverlay = overlayCache.get(cacheKey);
        if (cachedOverlay) {
          ctx.drawImage(cachedOverlay, 0, 0);
        }
      }

      // Draw this crop's border and fill
      const box = crop.getRect();
      const boxWidth = box.x2 - box.x1;
      const boxHeight = box.y2 - box.y1;
      ctx.save();
      ctx.globalCompositeOperation = 'source-over';
      ctx.fillStyle = 'rgba(255, 255, 255, 0.01)';
      ctx.fillRect(box.x1, box.y1, boxWidth, boxHeight);
      ctx.strokeStyle = '#cd3434';
      ctx.lineWidth = 1;
      ctx.strokeRect(box.x1, box.y1, boxWidth, boxHeight);
      ctx.restore();
    };
  });

  // Update ALL polygon zones on the page
  polygonZones.forEach((polygon) => {
    polygon.draw = (ctx: CanvasRenderingContext2D) => {
      // Check if this zone should draw the overlay (first by ID)
      const shouldDrawOverlay = polygon.Id === firstAnnotationId;

      if (shouldDrawOverlay) {
        // Draw the cached overlay using the correct cache key
        const cachedOverlay = overlayCache.get(cacheKey);
        if (cachedOverlay) {
          ctx.drawImage(cachedOverlay, 0, 0);
        }
      }

      // Draw this polygon's border and fill
      const points = (polygon as Core.Annotations.PolygonAnnotation).getPath();
      if (points && points.length > 0) {
        ctx.save();
        ctx.globalCompositeOperation = 'source-over';
        ctx.beginPath();
        ctx.moveTo(points[0].x, points[0].y);
        for (let i = 1; i < points.length; i++) {
          ctx.lineTo(points[i].x, points[i].y);
        }
        ctx.closePath();
        ctx.fillStyle = 'rgba(255, 255, 255, 0.01)';
        ctx.fill();
        ctx.strokeStyle = '#cd3434';
        ctx.lineWidth = 1;
        ctx.stroke();
        ctx.restore();
      }
    };
  });

  // When the first zone (by ID) is a ManualAlignCrop, wrap its draw to include the overlay first
  if (firstAnnotationId && manualAlignZones.some((z) => z.Id === firstAnnotationId)) {
    const overlayDrawer = manualAlignZones.find((z) => z.Id === firstAnnotationId);
    if (overlayDrawer) {
      // Restore all OTHER ManualAlignCrops (previous first may have been wrapped)
      manualAlignZones.forEach((maZone) => {
        if (maZone.Id !== overlayDrawer.Id) {
          const originalDraw = manualAlignOriginalDrawMap.get(maZone);
          if (originalDraw) {
            maZone.draw = originalDraw;
          }
        }
      });
      // Then wrap the new first
      let originalDraw = manualAlignOriginalDrawMap.get(overlayDrawer);
      if (!originalDraw) {
        const boundDraw = overlayDrawer.draw!.bind(overlayDrawer);
        originalDraw = (ctx: CanvasRenderingContext2D, pageMatrix?: any, pageRotation?: number, zoomLevel?: number) =>
          boundDraw(ctx, pageMatrix ?? null, pageRotation ?? 0, zoomLevel ?? 1);
        manualAlignOriginalDrawMap.set(overlayDrawer, originalDraw);
      }
      overlayDrawer.draw = (
        ctx: CanvasRenderingContext2D,
        pageMatrix?: any,
        pageRotation?: number,
        zoomLevel?: number,
      ) => {
        const cachedOverlay = overlayCache.get(cacheKey);
        if (cachedOverlay) {
          ctx.drawImage(cachedOverlay, 0, 0);
        }
        originalDraw!(ctx, pageMatrix, pageRotation, zoomLevel);
      };
    }
  } else {
    // First zone is a crop or polygon; restore ManualAlignZones to original draw so they don't draw overlay
    manualAlignZones.forEach((maZone) => {
      const originalDraw = manualAlignOriginalDrawMap.get(maZone);
      if (originalDraw) {
        maZone.draw = originalDraw;
      }
    });
  }

  if (refresh) {
    instance.Core.documentViewer.refreshAll();
    instance.Core.documentViewer.updateView();
  }
}
